import React from 'react';
import { cn } from '@/utils/classNames';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'glass' | 'gradient' | 'elevated';
  isInteractive?: boolean;
  hasShadow?: boolean;
  hasHoverEffect?: boolean;
  hasAnimation?: boolean;
  borderGlow?: boolean;
  header?: React.ReactNode;
  footer?: React.ReactNode;
}

export const Card: React.FC<CardProps> = ({
  children,
  className,
  variant = 'default',
  isInteractive = false,
  hasShadow = true,
  hasHoverEffect = false,
  hasAnimation = false,
  borderGlow = false,
  header,
  footer,
  ...props
}) => {
  // Base styles for all cards
  const baseStyles = 'rounded-xl overflow-hidden transition-all duration-300';

  // Variant-specific styles
  const variantStyles = {
    default: 'bg-surface-primary dark:bg-surface-primary',
    glass: 'glass dark:glass-dark backdrop-blur',
    gradient: 'bg-gradient-primary',
    elevated: 'bg-surface-secondary dark:bg-surface-secondary',
  };

  // Shadow styles
  const shadowStyles = hasShadow ? 'shadow-md' : '';

  // Interactive styles for clickable cards
  const interactiveStyles = isInteractive ? 'cursor-pointer' : '';

  // Hover effects
  const hoverStyles = hasHoverEffect ? 'hover:shadow-lg hover:-translate-y-1' : '';

  // Animation effects
  const animationStyles = hasAnimation ? 'animate-float' : '';

  // Border glow effect
  const glowStyles = borderGlow ? 'border border-primary border-glow' : '';

  return (
    <div
      className={cn(
        baseStyles,
        variantStyles[variant],
        shadowStyles,
        interactiveStyles,
        hoverStyles,
        animationStyles,
        glowStyles,
        className
      )}
      {...props}
    >
      {header && <div className="px-6 py-4 border-b border-divider">{header}</div>}

      <div className="p-6">{children}</div>

      {footer && (
        <div className="px-6 py-4 border-t border-divider bg-surface-secondary/20">{footer}</div>
      )}
    </div>
  );
};
